import { ApiClientError, type ApiClient } from "../../../infrastructure/http/apiClient";
import type {
  CreatePublicAppointmentInput,
  PublicAppointmentCreated,
  PublicResource,
  PublicService,
  PublicSlotsResponse
} from "../domain/publicBooking";

const forbiddenPublicFields = [
  "tenant_id",
  "user_id",
  "active",
  "public_booking_enabled",
  "billing",
  "capabilities",
  "created_at",
  "updated_at"
];

export async function getPublicServices(apiClient: ApiClient, tenantSlug: string): Promise<PublicService[]> {
  const response = await apiClient.get<unknown>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/services`
  );

  if (!isRecord(response) || !Array.isArray(response.services) || !response.services.every(isPublicService)) {
    throw invalidPublicResponse("services");
  }

  return response.services;
}

export async function getPublicResources(apiClient: ApiClient, tenantSlug: string): Promise<PublicResource[]> {
  const response = await apiClient.get<unknown>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/resources`
  );

  if (!isRecord(response) || !Array.isArray(response.resources) || !response.resources.every(isPublicResource)) {
    throw invalidPublicResponse("resources");
  }

  return response.resources;
}

export async function getPublicGeneralSlots(
  apiClient: ApiClient,
  tenantSlug: string,
  input: { serviceID: string; date: string }
): Promise<PublicSlotsResponse> {
  const params = new URLSearchParams({
    service_id: input.serviceID,
    date: input.date
  });
  const response = await apiClient.get<unknown>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/slots?${params.toString()}`
  );

  return parseSlotsResponse(response);
}

export async function getPublicResourceSlots(
  apiClient: ApiClient,
  tenantSlug: string,
  input: { resourceID: string; serviceID: string; date: string }
): Promise<PublicSlotsResponse> {
  const params = new URLSearchParams({
    service_id: input.serviceID,
    date: input.date
  });
  const response = await apiClient.get<unknown>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/resources/${encodeURIComponent(input.resourceID)}/slots?${params.toString()}`
  );

  return parseSlotsResponse(response);
}

export async function createPublicAppointment(
  apiClient: ApiClient,
  tenantSlug: string,
  input: CreatePublicAppointmentInput
): Promise<PublicAppointmentCreated> {
  const response = await apiClient.post<unknown, CreatePublicAppointmentInput>(
    `/api/v1/public/tenants/${encodeURIComponent(tenantSlug)}/appointments`,
    input
  );

  if (!isPublicAppointmentCreated(response)) {
    throw invalidPublicResponse("appointment");
  }

  return response;
}

function parseSlotsResponse(response: unknown): PublicSlotsResponse {
  if (!isRecord(response)
    || typeof response.date !== "string"
    || typeof response.timezone !== "string"
    || !Array.isArray(response.slots)
    || !response.slots.every(isPublicSlot)) {
    throw invalidPublicResponse("slots");
  }

  return response as PublicSlotsResponse;
}

function isPublicService(value: unknown): value is PublicService {
  return isRecord(value)
    && hasNoForbiddenPublicFields(value)
    && typeof value.id === "string"
    && typeof value.name === "string"
    && (value.description === undefined || typeof value.description === "string")
    && typeof value.duration_minutes === "number"
    && typeof value.price_cents === "number"
    && (value.sort_order === undefined || typeof value.sort_order === "number");
}

function isPublicResource(value: unknown): value is PublicResource {
  return isRecord(value)
    && hasNoForbiddenPublicFields(value)
    && typeof value.id === "string"
    && typeof value.name === "string"
    && typeof value.type === "string"
    && (value.avatar_url === undefined || typeof value.avatar_url === "string");
}

function isPublicSlot(value: unknown): value is PublicSlotsResponse["slots"][number] {
  return isRecord(value)
    && hasNoForbiddenPublicFields(value)
    && typeof value.start_time === "string"
    && typeof value.end_time === "string"
    && (value.resource_selection === undefined || typeof value.resource_selection === "string");
}

function isPublicAppointmentCreated(value: unknown): value is PublicAppointmentCreated {
  return isRecord(value)
    && isRecord(value.appointment)
    && hasNoForbiddenPublicFields(value.appointment)
    && typeof value.appointment.id === "string"
    && typeof value.appointment.status === "string"
    && typeof value.appointment.source === "string"
    && typeof value.appointment.start_time === "string"
    && typeof value.appointment.end_time === "string"
    && typeof value.appointment.timezone === "string";
}

function hasNoForbiddenPublicFields(value: Record<string, unknown>): boolean {
  return forbiddenPublicFields.every((field) => !(field in value));
}

function invalidPublicResponse(contract: string): ApiClientError {
  return new ApiClientError(`The public ${contract} response did not match the expected contract.`, {
    code: "invalid_response"
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
